import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { CheckCircle, Calendar, ClipboardList, DollarSign, Plus, MoreVertical } from 'lucide-react';

const PlanningSection: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const handleExplorePlanningTools = () => {
    if (user) {
      navigate('/plan');
    } else {
      navigate('/login');
    }
  };

  const kanbanColumns = [
    {
      title: 'To Do',
      color: 'bg-[#FF4E8E]/5',
      tasks: [
        { title: 'Book makeup artist', tag: 'Vendors', dueDate: '3 months' },
        { title: 'Order wedding favors', tag: 'Decor', dueDate: '2 months' },
        { title: 'Schedule final dress fitting', tag: 'Attire', dueDate: '1 month' }
      ]
    },
    {
      title: 'In Progress',
      color: 'bg-[#FF4E8E]/10',
      tasks: [
        { title: 'Design wedding invitations', tag: 'Stationery', dueDate: '2 weeks' },
        { title: 'Plan seating arrangement', tag: 'Planning', dueDate: '1 month' }
      ]
    },
    {
      title: 'Done',
      color: 'bg-[#FF4E8E]/15',
      tasks: [
        { title: 'Book wedding venue', tag: 'Venue', dueDate: 'Completed' },
        { title: 'Choose wedding dress', tag: 'Attire', dueDate: 'Completed' }
      ]
    }
  ];

  return (
    <section id="planning" className="py-20 relative overflow-hidden bg-white">
      <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full bg-[#FF4E8E]/5 blur-3xl"></div>
      
      <div className="container-custom relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-[42px] font-bold font-cormorant-garamond leading-[1.2] text-[#1C1C1E] mb-6">
            Your Journey to <span className="italic text-[#FF4E8E]">Wedding Perfection</span>
          </h2>
          <p className="text-[#535353] font-cormorant-garamond font-normal text-[16px] tracking-[0.1px]">
            Experience seamless wedding planning with our comprehensive suite of intelligent tools.
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-[#FF69B4]/15 to-[#FFDAB9]/15 p-4 sm:p-6 md:p-8 rounded-2xl mb-16">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Timeline and Budget Column */}
            <div className="lg:w-1/2 space-y-6">
              {/* Timeline Card */}
              <div className="bg-white p-4 sm:p-6 rounded-xl shadow-xl border border-[#FF4E8E]/20">
                <div className="flex items-center gap-3 mb-6">
                  <Calendar className="h-5 w-5 text-[#FF4E8E]" />
                  <h3 className="font-bold text-[20px] font-cormorant-garamond text-[#1C1C1E]">Wedding Timeline</h3>
                </div>
                <div className="space-y-6">
                  {[
                    { month: '12 months before', task: 'Select your perfect venue & set the date', highlight: true },
                    { month: '9 months before', task: 'Choose your wedding party & begin dress shopping' },
                    { month: '6 months before', task: 'Design & order your dream invitations' },
                    { month: '3 months before', task: 'Finalize menu & cake selections' },
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className={`w-3 h-3 rounded-full mt-1.5 ${item.highlight ? 'bg-[#FF4E8E]' : 'bg-[#FF4E8E]/40'}`}></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px] font-cormorant-garamond font-semibold text-[#FF4E8E]">{item.month}</p>
                        <p className="text-[15px] font-cormorant-garamond text-[#535353] break-words">{item.task}</p>
                      </div>
                      <CheckCircle className="h-5 w-5 text-[#FF4E8E]/70 flex-shrink-0" />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Budget Card */}
              <div className="bg-white p-4 sm:p-6 rounded-xl shadow-xl border border-[#FF4E8E]/20">
                <div className="flex items-center gap-3 mb-6">
                  <DollarSign className="h-5 w-5 text-[#FF4E8E]" />
                  <h3 className="font-bold text-[20px] font-cormorant-garamond text-[#1C1C1E]">Budget Overview</h3>
                </div>
                <div className="mb-6">
                  <div className="flex justify-between mb-2">
                    <span className="text-[15px] font-cormorant-garamond text-[#535353]">Wedding Budget</span>
                    <span className="font-semibold text-[#FF4E8E]">$25,000</span>
                  </div>
                  <div className="w-full bg-[#FF4E8E]/10 rounded-full h-2">
                    <div className="bg-[#FF4E8E] h-2 rounded-full" style={{ width: '65%' }}></div>
                  </div>
                </div>
                <div className="space-y-4">
                  {[
                    { category: 'Venue & Catering', amount: '$8,500', percentage: '34%' },
                    { category: 'Floral & Decor', amount: '$5,200', percentage: '21%' },
                    { category: 'Photography', amount: '$2,800', percentage: '11%' },
                  ].map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-[15px] font-cormorant-garamond text-[#535353]">{item.category}</span>
                      <div className="flex gap-4">
                        <span className="text-[#FF4E8E] font-medium">{item.amount}</span>
                        <span className="text-[#FF4E8E]/60 w-12 text-right">{item.percentage}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Kanban Board Column */}
            <div className="lg:w-1/2">
              <div className="bg-white p-4 sm:p-6 rounded-xl shadow-xl border border-[#FF4E8E]/20">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <ClipboardList className="h-5 w-5 text-[#FF4E8E]" />
                    <h3 className="font-bold text-[20px] font-cormorant-garamond text-[#1C1C1E]">Wedding Tasks</h3>
                  </div>
                  <button className="bg-gradient-to-r from-[#FF69B4] to-[#FF7F50] hover:opacity-90 text-white py-1.5 px-3 flex items-center gap-1 text-sm rounded-lg transition-all duration-300">
                    <Plus className="h-4 w-4" /> Add Task
                  </button>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 overflow-x-auto">
                  {kanbanColumns.map((column, columnIndex) => (
                    <div key={columnIndex} className={`${column.color} rounded-lg p-3 min-w-[250px] sm:min-w-0`}>
                      <h4 className="font-inter font-medium text-[14px] text-[#1C1C1E] mb-3">{column.title}</h4>
                      <div className="space-y-3">
                        {column.tasks.map((task, taskIndex) => (
                          <div key={taskIndex} className="bg-white/80 backdrop-blur-sm rounded-lg p-3 shadow-sm border border-[#FF4E8E]/10">
                            <div className="flex justify-between items-start gap-2 mb-2">
                              <h5 className="text-[#1C1C1E] font-medium text-sm line-clamp-2">{task.title}</h5>
                              <button className="text-[#535353] hover:text-[#FF4E8E] flex-shrink-0">
                                <MoreVertical className="h-4 w-4" />
                              </button>
                            </div>
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <span className="italic font-inter text-[12px] text-[#FF4E8E]">{task.tag}</span>
                              <span className="text-xs text-[#535353]">{task.dueDate}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-center">
          <button 
            onClick={handleExplorePlanningTools}
            className="bg-gradient-to-r from-[#FF69B4] to-[#FF7F50] hover:opacity-90 text-white px-8 py-4 rounded-lg transition-all duration-300 text-[18px] font-cormorant-garamond font-bold"
          >
            Explore All Planning Tools
          </button>
        </div>
      </div>
    </section>
  );
};

export default PlanningSection;